import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { SOCKET_URL, SOCKET_TRANSPORTS } from "src/_config";
import useSocket from "use-socket.io-client";

interface SocketContextType {
  socket: ReturnType<typeof useSocket>[0];
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket] = useSocket(SOCKET_URL, {
    autoConnect: false,
    withCredentials: true,
    transports: SOCKET_TRANSPORTS,
    upgrade: true, // Enable transport upgrades (polling -> websocket)
    rememberUpgrade: true, // Remember successful upgrades for faster reconnection
  });

  useEffect(() => {
    socket.connect();

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [socket]);

  return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>;
};

export const useSocketContext = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocketContext must be used within a SocketProvider");
  }
  return context;
};
